import type { IProfileListItem } from '@/src/interfaces/IProfileListItem'
import type { SelectItem } from '@/src/interfaces/components/ISelectComponent'
import type { IDropdownItem } from '@/src/interfaces/components/IDropdownComponent'
import { useDrawerStore, useUserStore } from '@/stores'
import { defineStore } from 'pinia'
import { factory } from '../../../../factory-api/factory'
import type {
  CandidateProfile,
  Item,
  JobGoal,
  Mobility,
  NextJobSection,
  ProfileJobType,
  SalarySetupValue,
  UpdateProfileRequest
} from '@/generated-api'
import { ProfileSectionEnum } from '../../../../generated-api'
import { profileDropdown } from '@/application/helpers/profileHelper'
import router from '@/router'

export const useNextJobStore = defineStore('nextJobStore', () => {
  // Constants and Refs
  const drawerStore = useDrawerStore()
  const userStore = useUserStore()
  const lastSaved = ref<Date | undefined>()

  const mobilityList = ref<SelectItem[]>([])
  const jobGoals = ref<IProfileListItem[]>([])
  const selectedJobFunctions = ref<IProfileListItem[]>([])
  const selectedJobTypes = ref<IProfileListItem[]>([])
  const selectedMobility = ref<SelectItem>()

  const saleryLevel = ref<number[]>([1, 10])
  const toggleSalarySlider = ref<boolean>(false)
  const isSaving = ref<boolean>(false)
  const isInitialized = ref<boolean>(false)
  const salaryLevelValues = ref<Record<number, string> | undefined>()

  // Methods/Functions
  const initialize = (item: NextJobSection) => {
    if (isInitialized.value === true) {
      return
    }

    selectedJobFunctions.value =
      item?.jobFunctions?.items?.map((jobfunction: Item) => ({
        id: Number(jobfunction.id),
        title: jobfunction.text ?? '',
        dropdownItems: profileDropdown().slice(3, 4),
        hide: false
      })) ?? []

    salaryLevelValues.value = item?.salaryLevel?.values?.reduce(
      (acc, item: SalarySetupValue) => {
        if (item.id !== undefined) {
          acc[item.id] = item.value ?? ''
        }
        return acc
      },
      {} as Record<number, string>
    )

    if (
      item?.salaryLevel?.selectedSalaryLevel &&
      item?.salaryLevel?.selectedSalaryLevel?.maxValue
    ) {
      toggleSalarySlider.value = true

      const minValueKey = item?.salaryLevel?.values?.find(
        (value: SalarySetupValue) => value.value === item.salaryLevel?.selectedSalaryLevel?.minValue
      )?.id
      const maxValueKey = item?.salaryLevel?.values?.find(
        (value: SalarySetupValue) => value.value === item.salaryLevel?.selectedSalaryLevel?.maxValue
      )?.id

      saleryLevel.value = [Number(minValueKey) || 0, Number(maxValueKey) || 1]
    }

    selectedJobTypes.value =
      item.jobTypes
        ?.filter((jobType: ProfileJobType) => jobType.selected === true)
        .map((jobType: ProfileJobType) => ({
          id: jobType.id ?? '',
          title: jobType.text ?? '',
          dropdownItems: profileDropdown().slice(3, 4),
          hide: false
        })) ?? []

    jobGoals.value =
      item?.jobGoals?.map((jobGoal: JobGoal) => ({
        id: jobGoal.id ?? 0,
        title: jobGoal.text ?? '',
        isPublic: jobGoal.selected,
        dropdownItems: profileDropdown().filter(
          (item: IDropdownItem) =>
            (jobGoal.selected === true ? item.icon === 'eye-slash' : item.icon === 'eye') ||
            item.icon === 'pencil'
        ),
        hide: false
      })) ?? []

    item?.mobility?.forEach((mobility: Mobility) => {
      if (mobility.selected === true) {
        selectedMobility.value = {
          id: mobility.id,
          name: mobility.text ?? '',
          icon: mobilityIcon(mobility.name ?? '')
        }
      }

      mobilityList.value.push({
        id: mobility.id ?? '',
        name: mobility.text ?? '',
        icon: mobilityIcon(mobility.name ?? '')
      })
    })

    isInitialized.value = true
  }

  const toggleSlider = () => {
    toggleSalarySlider.value = !toggleSalarySlider.value
  }

  const addJobFunction = (jobFunction: SelectItem) => {
    if (
      !selectedJobFunctions.value.some((existing) => existing.id === jobFunction.id) &&
      selectedJobFunctions.value.length < 6
    ) {
      selectedJobFunctions.value.push({
        id: Number(jobFunction.id),
        title: jobFunction.name,
        dropdownItems: profileDropdown().slice(3, 4),
        hide: false
      })
    }

    drawerStore.closeSubDrawer()
    drawerStore.setCanCloseDrawer(false)
  }

  const updateSelectedJobTypes = (jobTypes: SelectItem[]) => {
    selectedJobTypes.value = jobTypes.map((jobType) => ({
      id: jobType.id ?? 0,
      title: jobType.name,
      dropdownItems: profileDropdown().slice(3, 4),
      hide: false
    }))

    drawerStore.closeSubDrawer()
    drawerStore.setCanCloseDrawer(false)
  }

  const mobilityIcon = (mobilityName: string): string => {
    if (mobilityName == 'localMobility') {
      return 'bicycle'
    } else if (mobilityName == 'commuteMobility') {
      return 'shipping-fast'
    } else if (mobilityName == 'moveMobility') {
      return 'home'
    } else {
      return ''
    }
  }

  const selectMobility = (mobility: SelectItem) => {
    selectedMobility.value = mobility
    drawerStore.setCanCloseDrawer(false)
  }

  const handleJobGoalDropdown = (jobGoalId: number, action: string, jobGoalLink: string) => {
    if (action === 'setPublic' || action === 'setPrivate') {
      const jobGoal = jobGoals.value.find((goal) => goal.id == jobGoalId)

      if (jobGoal != undefined) {
        jobGoal.isPublic = !jobGoal.isPublic
        jobGoal.dropdownItems = profileDropdown().filter(
          (item: IDropdownItem) =>
            (jobGoal.isPublic === true ? item.icon === 'eye-slash' : item.icon === 'eye') ||
            item.icon === 'pencil'
        )
      }
      drawerStore.setCanCloseDrawer(false)
    } else if (action === 'edit') {
      router.push(jobGoalLink ?? '')
    }
  }

  const deleteJobFunction = (jobFunctionId: number) => {
    selectedJobFunctions.value = selectedJobFunctions.value.filter(
      (jobFunction) => jobFunction.id !== jobFunctionId
    )
    drawerStore.setCanCloseDrawer(false)
  }

  const deleteJobType = (jobTypeId: number) => {
    selectedJobTypes.value = selectedJobTypes.value.filter((type) => type.id !== jobTypeId)
    drawerStore.setCanCloseDrawer(false)
  }

  const saveNextJobSection = async () => {
    isSaving.value = true

    const publicJobGoals = jobGoals.value.filter((goal) => goal.isPublic == true)

    await saveCandiateProfile({
      id: userStore.member.id,
      jobFunctions: selectedJobFunctions.value.map((jobFunction) => Number(jobFunction.id)),
      jobGoals: publicJobGoals.map((jobGoals) => Number(jobGoals.id)),
      mobility: selectedMobility.value?.id ? Number(selectedMobility.value.id) : undefined,
      jobTypes: selectedJobTypes.value.map((jobType) => Number(jobType.id)),
      salaryLevel:
        toggleSalarySlider.value === true
          ? {
              minId: saleryLevel.value[0],
              maxId: saleryLevel.value[1]
            }
          : undefined
    })

    lastSaved.value = new Date()
    isSaving.value = false
  }

  const saveCandiateProfile = async (candidateProfile: CandidateProfile) => {
    const profileRequest: UpdateProfileRequest = {
      onlyPersonalData: false,
      profileSection: ProfileSectionEnum.NextJob,
      candidateProfile: candidateProfile
    }

    const api = await factory.getMemberApi()
    await api.updateProfile(profileRequest)
  }

  const setIsInitialized = (toggle: boolean) => {
    isInitialized.value = toggle
  }

  return {
    mobilityList,
    jobGoals,
    selectedJobFunctions,
    selectedMobility,
    saleryLevel,
    toggleSalarySlider,
    isSaving,
    selectedJobTypes,
    lastSaved,
    deleteJobFunction,
    handleJobGoalDropdown,
    deleteJobType,
    initialize,
    selectMobility,
    updateSelectedJobTypes,
    toggleSlider,
    saveNextJobSection,
    addJobFunction,
    saveCandiateProfile,
    salaryLevelValues,
    isInitialized,
    setIsInitialized
  }
})
